import { defaultSysCode_Modal, SysCodeDto } from "src/dtos/code/sysCodeDto";
import { HRM3040_R01ServiceAllowDto, HRM3040_R01PositionAllowDto } from "./Dto_HRM3040_R01";

/** 등록·삭제 버튼의 대상 편집 그리드이다. */
export type HRM3040_R01GridType = 'service' | 'position';
/** 종료일 미지정 기준의 열린 끝 날짜이다. */
export const HRM3040_R01_OPEN_END_DT = '9999-12-31';

/** 표시 콤보의 업무 선택지. 빈 값의 '전체'는 공통 SearchDropDownForm이 제공한다(사용자 확정). */
export const HRM3040_R01_SCOPE_LIST: SysCodeDto[] = [
    { ...defaultSysCode_Modal, minorCd: 'cur', codeNm: '기준일 적용분' },
];

/** 근속수당 기준 컬럼 제목이다. */
export const serviceHeaderNames_HRM3040_R01 = {
    rowIndex: '', startDt: '적용시작', endDt: '적용종료', fromYear: '근속 이상', toYear: '미만', allowAmt: '근속수당', useYn: '사용', remark: '비고',
};

/** 직책수당 기준 컬럼 제목이다. */
export const positionHeaderNames_HRM3040_R01 = {
    rowIndex: '', startDt: '적용시작', endDt: '적용종료', jobPosition: '직책', allowAmt: '직책수당', useYn: '사용', remark: '비고',
};

/** 서버 계산 미리보기 컬럼 제목이다. */
export const previewHeaderNames_HRM3040_R01 = {
    rowIndex: '', empNo: '사원번호', empNm: '사원명', orgNm: '부서', empDt: '입사일자', serviceYears: '근속(만)',
    serviceAllowAmt: '근속수당', jobPosition: '직책', positionAllowAmt: '직책수당', totalAllowAmt: '합계(월)',
};

/** YYYY-MM-DD 형식과 실제 달력 날짜가 맞으면 true를 반환한다. */
const isValidHRM3040_R01Date = (value: string): boolean => {
    // 정규식은 네 자리 연도·두 자리 월·두 자리 일이 하이픈으로 연결되었는지 검사한다.
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || value < '0001-01-01') return false;
    const date = new Date(`${value}T00:00:00Z`); // 존재하지 않는 일자를 자동 보정했는지 확인할 날짜
    return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
};

/** 두 편집 그리드의 삭제 제외 행을 검증해 첫 오류 문장, 모두 유효하면 null을 반환한다. 전체 이력 겹침은 서버가 최종 검사한다. */
export const validateHRM3040_R01SaveData = (
    serviceRows: HRM3040_R01ServiceAllowDto[], positionRows: HRM3040_R01PositionAllowDto[],
): string | null => {
    /** 같은 형식의 날짜는 문자열 순서가 날짜 순서와 같으므로 문자열로 적용기간을 비교한다. */
    const validateDates = (startDt: string, endDt: string, label: string): string | null => {
        if (!isValidHRM3040_R01Date(startDt) || !isValidHRM3040_R01Date(endDt))
            return `적용시작일과 종료일을 입력해주세요 (${label})`;
        if (endDt < startDt) return `적용종료일은 시작일보다 빠를 수 없습니다 (${label})`;
        return null;
    };

    /** 공통 금액·사용여부·비고를 검사해 첫 오류 또는 null을 반환한다. */
    const validateValues = (row: HRM3040_R01ServiceAllowDto | HRM3040_R01PositionAllowDto, label: string): string | null => {
        if (row.allowAmt == null || !Number.isFinite(row.allowAmt) || row.allowAmt < 0) return `금액을 확인해주세요 (${label})`;
        if (row.useYn !== 'Y' && row.useYn !== 'N') return `사용여부는 Y 또는 N이어야 합니다 (${label})`;
        // ?.·??: 비고가 null이면 길이를 0으로 검사한다.
        if ((row.remark?.length ?? 0) > 1000) return `비고는 1000자 이하로 입력해주세요 (${label})`;
        return null;
    };

    /** 근속 행 식별 문구를 서버와 같은 시작일, N년으로 반환한다. */
    const describeService = (row: HRM3040_R01ServiceAllowDto) => `${row.startDt ?? ''}, ${row.fromYear ?? ''}년`;

    /** 직책 행 식별 문구를 코드명 대신 원본 코드로 반환한다. */
    const describePosition = (row: HRM3040_R01PositionAllowDto) => `${row.startDt ?? ''}, ${row.jobPosition ?? ''}`;
    for (const row of serviceRows) {
        const label = describeService(row); // 오류가 발생한 기준 식별
        const dateError = validateDates(row.startDt, row.endDt, label); // 날짜 검증 결과
        if (dateError) return dateError;
        if (!Number.isInteger(row.fromYear) || !Number.isInteger(row.toYear) || row.fromYear < 0 || row.toYear < 0)
            return `근속 년수를 확인해주세요 (${label})`;
        if (row.toYear <= row.fromYear) return `근속 미만 년수는 이상 년수보다 커야 합니다 (${label})`;
        const valueError = validateValues(row, label); // 공통 값 검증 결과
        if (valueError) return valueError;
    }
    for (const row of positionRows) {
        const label = describePosition(row); // 오류 기준의 시작일과 직책코드
        const dateError = validateDates(row.startDt, row.endDt, label); // 날짜 검증 결과
        if (dateError) return dateError;
        if (!row.jobPosition?.trim()) return `직책을 선택해주세요 (${label})`;
        const valueError = validateValues(row, label); // 공통 값 검증 결과
        if (valueError) return valueError;
    }
    // 각 목록의 i < j 조합만 검사해 자기 자신과 비교하지 않는다. 기간은 양 끝 포함, 근속 미만 경계는 제외한다.
    for (let i = 0; i < serviceRows.length; i++) {
        const a = serviceRows[i]; // 첫 근속 비교 행
        if (a.useYn !== 'Y') continue;
        for (let j = i + 1; j < serviceRows.length; j++) {
            const b = serviceRows[j]; // 다음 근속 비교 행
            if (b.useYn === 'Y' && a.startDt <= b.endDt && b.startDt <= a.endDt && a.fromYear < b.toYear && b.fromYear < a.toYear)
                return `근속 구간이 겹칩니다: ${describeService(a)} · ${describeService(b)}`;
        }
    }
    for (let i = 0; i < positionRows.length; i++) {
        const a = positionRows[i]; // 첫 직책 비교 행
        if (a.useYn !== 'Y') continue;
        for (let j = i + 1; j < positionRows.length; j++) {
            const b = positionRows[j]; // 다음 직책 비교 행
            if (b.useYn === 'Y' && a.jobPosition === b.jobPosition && a.startDt <= b.endDt && b.startDt <= a.endDt)
                return `직책수당 기간이 겹칩니다: ${describePosition(a)} · ${describePosition(b)}`;
        }
    }
    return null;
};
